import * as THREE from 'three';
import {
  ROOM_WIDTH, ROOM_DEPTH, ROOM_HEIGHT,
  FLOOR_COLOR, WALL_COLOR, BASEBOARD_COLOR,
} from '@/utils/constants';

/**
 * Creates the office room shell: 3 walls, wood floor, baseboards.
 * No front wall or ceiling — camera looks in from above-front.
 */
export function createOfficeRoom(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'office-room';

  const wallMat = new THREE.MeshStandardMaterial({ color: WALL_COLOR, roughness: 0.9 });
  const floorMat = new THREE.MeshStandardMaterial({ color: FLOOR_COLOR, roughness: 0.7 });
  const baseboardMat = new THREE.MeshStandardMaterial({ color: BASEBOARD_COLOR, roughness: 0.8 });

  // ── Floor ────────────────────────────────────────────────
  const floorGeo = new THREE.BoxGeometry(ROOM_WIDTH, 0.1, ROOM_DEPTH);
  const floor = new THREE.Mesh(floorGeo, floorMat);
  floor.position.y = -0.05;
  floor.receiveShadow = true;
  floor.name = 'floor';
  group.add(floor);

  // ── Back Wall (Z = -ROOM_DEPTH/2) ───────────────────────
  const backWallGeo = new THREE.BoxGeometry(ROOM_WIDTH, ROOM_HEIGHT, 0.1);
  const backWall = new THREE.Mesh(backWallGeo, wallMat);
  backWall.position.set(0, ROOM_HEIGHT / 2, -ROOM_DEPTH / 2);
  backWall.receiveShadow = true;
  backWall.name = 'back-wall';
  group.add(backWall);

  // ── Left Wall (X = -ROOM_WIDTH/2) ───────────────────────
  const leftWallGeo = new THREE.BoxGeometry(0.1, ROOM_HEIGHT, ROOM_DEPTH);
  const leftWall = new THREE.Mesh(leftWallGeo, wallMat);
  leftWall.position.set(-ROOM_WIDTH / 2, ROOM_HEIGHT / 2, 0);
  leftWall.receiveShadow = true;
  leftWall.name = 'left-wall';
  group.add(leftWall);

  // ── Right Wall (X = ROOM_WIDTH/2) ───────────────────────
  const rightWallGeo = new THREE.BoxGeometry(0.1, ROOM_HEIGHT, ROOM_DEPTH);
  const rightWall = new THREE.Mesh(rightWallGeo, wallMat);
  rightWall.position.set(ROOM_WIDTH / 2, ROOM_HEIGHT / 2, 0);
  rightWall.receiveShadow = true;
  rightWall.name = 'right-wall';
  group.add(rightWall);

  // ── Baseboards ───────────────────────────────────────────
  const bbHeight = 0.08;
  const bbThickness = 0.02;

  // Back wall baseboard
  const bbBackGeo = new THREE.BoxGeometry(ROOM_WIDTH, bbHeight, bbThickness);
  const bbBack = new THREE.Mesh(bbBackGeo, baseboardMat);
  bbBack.position.set(0, bbHeight / 2, -ROOM_DEPTH / 2 + 0.06);
  group.add(bbBack);

  // Left wall baseboard
  const bbLeftGeo = new THREE.BoxGeometry(bbThickness, bbHeight, ROOM_DEPTH);
  const bbLeft = new THREE.Mesh(bbLeftGeo, baseboardMat);
  bbLeft.position.set(-ROOM_WIDTH / 2 + 0.06, bbHeight / 2, 0);
  group.add(bbLeft);

  // Right wall baseboard
  const bbRightGeo = new THREE.BoxGeometry(bbThickness, bbHeight, ROOM_DEPTH);
  const bbRight = new THREE.Mesh(bbRightGeo, baseboardMat);
  bbRight.position.set(ROOM_WIDTH / 2 - 0.06, bbHeight / 2, 0);
  group.add(bbRight);

  return group;
}
